/**
 * Ícones das 7 perícias.
 *
 * <p>O protótipo usava glifos de fonte (⚔ ☘ ✂ ⛏ ⚗ ❖ ⚒) como reconhecido placeholder.
 * Eles têm dois problemas no mapa: o desenho muda conforme a fonte do sistema, e em
 * tamanho pequeno vários viram a mesma mancha.
 *
 * <p>Estes são desenhos próprios, feitos para 14px: silhuetas simples e distintas
 * entre si, sem detalhe que suma ao encolher. A cor vem da perícia, então forma e
 * cor identificam juntas — quem não distingue as cores ainda distingue as formas.
 */
interface Props {
  pericia: string;
  cor?: string;
  tamanho?: number;
  titulo?: string;
}

export function PericiaIcone({ pericia, cor = 'currentColor', tamanho = 14, titulo }: Props) {
  const comum = {
    width: tamanho,
    height: tamanho,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: cor,
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };

  return (
    <svg {...comum} role="img" aria-label={titulo ?? pericia}>
      {titulo && <title>{titulo}</title>}
      {desenho(pericia, cor)}
    </svg>
  );
}

function desenho(pericia: string, cor: string) {
  switch (pericia) {
    // Espadas cruzadas.
    case 'combate':
      return (
        <>
          <path d="M4 4l11 11" />
          <path d="M20 4L9 15" />
          <path d="M14 17l3 3" />
          <path d="M10 17l-3 3" />
        </>
      );

    // Folha com nervura.
    case 'herbalismo':
      return (
        <>
          <path d="M5 19C5 11 11 5 19 5c0 8-6 14-14 14z" />
          <path d="M5 19c4-4 7-6 12-8" />
        </>
      );

    // Lâmina curva de esfola.
    case 'esfolagem':
      return (
        <>
          <path d="M4 18c6 2 14-3 16-13" />
          <path d="M4 18l4 2" />
          <path d="M20 5l-4 1" />
        </>
      );

    // Picareta.
    case 'mineracao':
      return (
        <>
          <path d="M3 8c6-4 12-4 18 0" />
          <path d="M12 6v14" />
        </>
      );

    // Frasco de alquimia.
    case 'alquimia':
      return (
        <>
          <path d="M10 3v6l-5 9a2 2 0 002 3h10a2 2 0 002-3l-5-9V3" />
          <path d="M9 3h6" />
        </>
      );

    // Couro esticado.
    case 'couraria':
      return (
        <>
          <path d="M12 3l7 5-3 13H8L5 8z" />
          <path d="M9 12h6" />
        </>
      );

    // Martelo sobre bigorna.
    case 'forja':
      return (
        <>
          <path d="M4 9h9l4-4 3 3-4 4v2H4z" />
          <path d="M6 20h12" />
          <path d="M9 14v6" fill={cor} />
        </>
      );

    // Engrenagem com faísca: metal que pensa.
    case 'tecnomagia':
      return (
        <>
          <circle cx="11" cy="13" r="4" />
          <path d="M11 5v2M11 19v2M3 13h2M17 13h2M5.5 7.5l1.5 1.5M15 17l1.5 1.5M16.5 7.5L15 9M7 17l-1.5 1.5" />
          <path d="M19 3l1 2 2 1-2 1-1 2-1-2-2-1 2-1z" fill={cor} />
        </>
      );

    /*
     * As de Combate. Elas só aparecem na Ficha, e por isso a leitura acontece em
     * tamanho grande — mas o desenho segue a mesma regra das outras: silhueta que
     * sobrevive ao encolher, sem detalhe interno.
     */

    // Punho fechado.
    case 'desarmado':
      return (
        <>
          <path d="M6 11V7a2 2 0 014 0v4" />
          <path d="M10 11V6a2 2 0 014 0v5" />
          <path d="M14 11V8a2 2 0 014 0v6a6 6 0 01-6 6h-1a5 5 0 01-5-5v-4" />
        </>
      );

    // Lâmina reta com guarda.
    case 'espada':
      return (
        <>
          <path d="M12 3l2 3v9h-4V6z" />
          <path d="M8 15h8" />
          <path d="M12 15v6" />
        </>
      );

    // Cabeça de machado num cabo.
    case 'machado':
      return (
        <>
          <path d="M13 4c4 0 7 3 7 6h-7z" />
          <path d="M13 4v6" />
          <path d="M12 10L5 21" />
        </>
      );

    // Lança: haste longa com ponta.
    case 'haste':
      return (
        <>
          <path d="M16 3l3 3-3 3-2-2z" />
          <path d="M14 7L4 20" />
          <path d="M6 15l3 3" />
        </>
      );

    // Maça: cabo e cabeça cravada.
    case 'maca':
      return (
        <>
          <circle cx="15" cy="8" r="4" />
          <path d="M15 2v1M21 8h1M15 13v1M9 8h1M17.8 5.2l.7-.7M17.8 10.8l.7.7" />
          <path d="M12 11L4 20" />
        </>
      );

    // Arco com corda e flecha.
    case 'arco':
      return (
        <>
          <path d="M7 3c7 3 7 15 0 18" />
          <path d="M7 3v18" />
          <path d="M7 12h12" />
          <path d="M16 9l3 3-3 3" />
        </>
      );

    /*
     * Magia Ofensiva: a fagulha lançada — o núcleo cheio, com quatro pontas em cruz.
     *
     * Entrou no lugar de Duas Mãos e Arma Exótica. O núcleo é preenchido de propósito:
     * a Magia Defensiva (`arcana`) é um escudo contornado, e a diferença entre massa e
     * contorno é o que separa as duas a 20px, antes de o olho ler a forma.
     */
    case 'magiaOfensiva':
      return (
        <>
          <circle cx="12" cy="12" r="3.4" fill={cor} stroke="none" />
          <path d="M12 2.5v4M12 17.5v4M2.5 12h4M17.5 12h4" />
          <path d="M6.2 6.2l2.4 2.4M15.4 15.4l2.4 2.4M17.8 6.2l-2.4 2.4M8.6 15.4l-2.4 2.4" />
        </>
      );


    // Besta: arco na horizontal sobre a coronha.
    case 'besta':
      return (
        <>
          <path d="M4 7c4-3 12-3 16 0" />
          <path d="M12 5v14" />
          <path d="M6 13h12" />
        </>
      );

    // Esquiva: silhueta que sai do lugar, com o rastro atrás.
    case 'esquiva':
      return (
        <>
          <path d="M16 4a2 2 0 11-.001-.001" />
          <path d="M18 21l-3-6-3-2 1-5" />
          <path d="M13 8l-3 2-1 4" />
          <path d="M3 9h4M2 13h4" />
        </>
      );

    // Escudo.
    case 'bloqueio':
      return (
        <>
          <path d="M12 3l8 3v6c0 5-4 8-8 9-4-1-8-4-8-9V6z" />
          <path d="M12 3v18" />
        </>
      );

    // Arcana: o selo que se ergue contra o golpe.
    case 'arcana':
      return (
        <>
          <path d="M12 3l8 5v8l-8 5-8-5V8z" />
          <path d="M12 8v8M8 10.5v3M16 10.5v3" />
        </>
      );

    default:
      return <circle cx="12" cy="12" r="7" />;
  }
}
